// ?use_ios_ui — twin of iOS 0.7.0 PanelSlider + SliderThumb: a horizontal
// track over [min, max] in whole steps, accent up to the thumb, dim after.
// A touch anywhere on the track moves the thumb there.

import { useCallback, useRef, type PointerEvent } from 'react'
import { IOS_UI_PALETTE } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'

const THUMB_PX = 20

/** The system slider's white knob, for the light question card. */
function LightThumb() {
  return (
    <div
      style={{
        width: THUMB_PX + 6,
        height: THUMB_PX + 6,
        margin: -3,
        borderRadius: '50%',
        background: '#FFFFFF',
        boxShadow: '0 0.5px 4px rgba(0,0,0,0.12), 0 3px 8px rgba(0,0,0,0.15)',
      }}
    />
  )
}

/** An accent dot inside an accent ring, with a gap of panel between them. */
export function SliderThumb() {
  return (
    <div
      style={{
        width: THUMB_PX,
        height: THUMB_PX,
        boxSizing: 'border-box',
        borderRadius: '50%',
        border: `1.5px solid ${IOS_UI_PALETTE.accentOnDark}`,
        background: IOS_UI_PALETTE.panelBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ width: 12, height: 12, borderRadius: '50%', background: IOS_UI_PALETTE.accentOnDark }} />
    </div>
  )
}

interface PanelSliderProps {
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  ariaLabel: string
  testId: string
  /** Light backdrop (the size question card) wants the plain accent and
   *  a grey track — the panel's white-on-black track vanishes on white. */
  accent?: string
  trackColor?: string
  thumb?: 'panel' | 'light'
}

export function PanelSlider({
  value,
  min,
  max,
  step,
  onChange,
  ariaLabel,
  testId,
  accent = IOS_UI_PALETTE.accentOnDark,
  trackColor = IOS_UI_PALETTE.track,
  thumb = 'panel',
}: PanelSliderProps) {
  const dragging = useRef(false)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const valueAt = useCallback(
    (el: HTMLElement, clientX: number) => {
      const rect = el.getBoundingClientRect()
      const travel = Math.max(1, rect.width - THUMB_PX)
      const position = Math.min(Math.max((clientX - rect.left - THUMB_PX / 2) / travel, 0), 1)
      const raw = min + position * (max - min)
      return min + Math.round((raw - min) / step) * step
    },
    [min, max, step],
  )

  const handleDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragging.current = true
      e.currentTarget.setPointerCapture?.(e.pointerId)
      onChangeRef.current(valueAt(e.currentTarget, e.clientX))
    },
    [valueAt],
  )

  const handleMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return
      onChangeRef.current(valueAt(e.currentTarget, e.clientX))
    },
    [valueAt],
  )

  const handleEnd = useCallback(() => {
    dragging.current = false
  }, [])

  const fraction = Math.min(Math.max((value - min) / (max - min), 0), 1)

  return (
    <div
      data-testid={testId}
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleEnd}
      onPointerCancel={handleEnd}
      style={{ position: 'relative', flex: 1, minWidth: 60, height: 28, touchAction: 'none', cursor: 'pointer' }}
    >
      <div style={{ position: 'absolute', left: 0, right: 0, top: 12.75, height: 2.5, borderRadius: 2, background: trackColor }} />
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 12.75,
          height: 2.5,
          borderRadius: 2,
          width: `calc(${fraction} * (100% - ${THUMB_PX}px) + ${THUMB_PX / 2}px)`,
          background: accent,
        }}
      />
      <div style={{ position: 'absolute', top: 4, left: `calc(${fraction} * (100% - ${THUMB_PX}px))` }}>
        {thumb === 'light' ? <LightThumb /> : <SliderThumb />}
      </div>
    </div>
  )
}
